import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { OrderCreatedDto, StatusDtoOrderStatusEnum } from 'utils/types/OrderModels';

import Button from 'components/atom/Button';
import { IconEnum } from 'components/common/Icon';

import { getPurchaseInfoMessage, getPurchaseStatusInfoTitle } from './purchaseHelperFunctions';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 60vh;
    padding: 40px 20px;
    text-align: center;
`;

const StatusCard = styled.div`
    background: var(--color-white);
    border-radius: 10px;
    padding: 40px;
    max-width: 600px;
    width: 100%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

export enum UIStatus {
    SUCCESS = 'success',
    ERROR = 'error',
    PROCESSING = 'processing',
    POLLING = 'polling'
}

const getUIStatus = (apiStatus: StatusDtoOrderStatusEnum, isPolling?: boolean): UIStatus => {
    if (isPolling) return UIStatus.POLLING;

    switch (apiStatus) {
    case StatusDtoOrderStatusEnum.PAID:
    case StatusDtoOrderStatusEnum.COMPLETED:
        return UIStatus.SUCCESS;
    case StatusDtoOrderStatusEnum.FAILED:
    case StatusDtoOrderStatusEnum.CANCELLED:
    case StatusDtoOrderStatusEnum.EXPIRED:
        return UIStatus.ERROR;
    case StatusDtoOrderStatusEnum.PENDING:
    case StatusDtoOrderStatusEnum.REGISTERED:
    default:
        return UIStatus.PROCESSING;
    }
};

const IconContainer = styled.div<{ uiStatus: UIStatus }>`
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    
    svg {
        width: 80px;
        height: 80px;
        color: ${(props) => {
            switch (props.uiStatus) {
            case 'success': return 'var(--color-success)';
            case 'error': return 'var(--color-error)';
            case 'processing':
            case 'polling':
            default: return 'var(--color-primary)';
            }
        }};
    }
`;

const Title = styled.h1<{ uiStatus: UIStatus }>`
    font-size: 24px;
    margin-bottom: 20px;
    color: ${(props) => {
        switch (props.uiStatus) {
        case 'success': return 'var(--color-success)';
        case 'error': return 'var(--color-error)';
        default: return 'var(--color-primary)';
        }
    }};
    font-weight: 600;
`;

const StatusMessage = styled.p`
    font-size: 18px;
    margin-bottom: 20px;
    color: var(--color-primary);
    font-weight: 500;
`;

const InfoMessage = styled.div<{ uiStatus: UIStatus }>`
    background: ${(props) => {
        switch (props.uiStatus) {
        case 'success': return 'var(--color-success-background)';
        case 'error': return 'var(--color-error-background)';
        default: return 'var(--color-alt-second)';
        }
    }};
    border: 1px solid ${(props) => {
        switch (props.uiStatus) {
        case 'success': return 'var(--color-success)';
        case 'error': return 'var(--color-error)';
        default: return 'var(--color-alt)';
        }
    }};
    border-radius: 8px;
    padding: 15px;
    margin: 20px 0 40px 0;
    color: ${(props) => {
        switch (props.uiStatus) {
        case 'success': return 'var(--color-success)';
        case 'error': return 'var(--color-error)';
        default: return 'var(--color-alt)';
        }
    }};
    font-size: 16px;
    line-height: 1.5;
    display: flex;
    flex-direction: column;
    span {
        color: inherit;
        font-size: 16px;
    }
`;

const OrderDetails = styled.div`
    background: var(--color-alt-second);
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
    text-align: left;
`;

const OrderItem = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 14px;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 30px;
    
    @media (min-width: 576px) {
        flex-direction: row;
        justify-content: center;
    }
`;

const SimpleSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid var(--color-alt-second);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

export enum PurchaseProductEnum {
    SUBSCRIPTION = 'subscription',
    SMS = 'sms',
    TOKENS = 'tokens'
}

interface PurchaseStatusInfoProps {
    purchaseType: PurchaseProductEnum;
    status: StatusDtoOrderStatusEnum;
    processStep?: string;
    orderData?: OrderCreatedDto;
    isNewAccount?: boolean;
    error?: string;
    canRetry?: boolean;
    isLoading?: boolean;
    isPolling?: boolean;
    onCancel?: () => void;
    onGoHome?: () => void;
    onCheckStatus?: () => void;
}

const PurchaseStatusInfo: FC<PurchaseStatusInfoProps> = ({
    purchaseType,
    status,
    processStep,
    orderData,
    isNewAccount = false,
    error,
    isLoading = false,
    isPolling = false,
    onCheckStatus
}) => {
    const navigate = useNavigate();
    const [isChecking, setIsChecking] = useState(false);

    const uiStatus = getUIStatus(status, isPolling);
    const title = getPurchaseStatusInfoTitle(status, purchaseType, isPolling, isNewAccount);

    const checkPaymentStatus = async () => {
        if (onCheckStatus) {
            setIsChecking(true);
            await onCheckStatus();
            setIsChecking(false);
        }
    };

    useEffect(() => {
        if (status === StatusDtoOrderStatusEnum.COMPLETED && purchaseType === 'subscription') {
            const timer = setTimeout(() => {
                navigate('/');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [
        status,
        purchaseType,
        navigate
    ]);

    const handleGoToSettings = () => {
        navigate('/my-settings');
    };

    const infoMessage = getPurchaseInfoMessage(purchaseType, uiStatus, error);

    return (
        <Container>
            <StatusCard>
                {(uiStatus === 'processing' || uiStatus === 'polling') &&
                    <IconContainer uiStatus={uiStatus}>
                        <SimpleSpinner />
                    </IconContainer>
                }

                <Title uiStatus={uiStatus}>
                    {title}
                </Title>

                {processStep &&
                    <StatusMessage>
                        {processStep}
                    </StatusMessage>
                }

                <InfoMessage uiStatus={uiStatus}>
                    <strong>{infoMessage.title}</strong><br/>
                    <span>{infoMessage.content}</span>
                </InfoMessage>
                {orderData && uiStatus === 'success' &&
                    <OrderDetails>
                        <OrderItem>
                            <span>Numer zamówienia:</span>
                            <span>{orderData.orderNumer}</span>
                        </OrderItem>
                        <OrderItem>
                            <span>Data zakupu:</span>
                            <span>{new Date().toLocaleDateString()}</span>
                        </OrderItem>
                    </OrderDetails>
                }

                <ButtonsContainer>
                    {uiStatus === 'success' &&
                        <Button onClick={handleGoToSettings}>
                            Moje ustawienia
                        </Button>
                    }

                    {uiStatus === 'error' &&
                        <>
                            <Button onClick={handleGoToSettings} secondary>
                                Moje ustawienia
                            </Button>
                        </>
                    }

                    {(uiStatus === 'polling' || uiStatus === 'processing') &&
                        <>
                            {onCheckStatus &&
                                <Button
                                    onClick={checkPaymentStatus}
                                    disabled={isChecking || isLoading}
                                    actionIcon={IconEnum.ARROW_RIGHT}
                                >
                                    {isChecking ? 'Sprawdzam...' : 'Sprawdź status płatności'}
                                </Button>}
                            <Button onClick={handleGoToSettings} secondary>
                                Moje ustawienia
                            </Button>
                        </>
                    }
                </ButtonsContainer>

            </StatusCard>
        </Container>
    );
};

export default PurchaseStatusInfo;

